import dayjs from 'dayjs';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TouchableOpacity } from 'react-native';

import CachedImage from '@components/CachedImage/CachedImage';
import Highlighter from '@components/HighlightWords';
import { useConfirmModal } from '@components/Modals/MobileModal';
import RadioButton from '@components/RadioButton/RadioButton';
import { Box, Text } from '@components/Restyle';
import GlobalSearchList from '@components/Search/GlobalSearchList';
import Icon from '@components/shared/Icon/Icon';
import { TaskRowSkeleton } from '@components/shared/skeletons';
import { LocalFile, Task } from '@graphql/generated';
import useMe from '@hooks/useMe';
import { getTaskMembership } from '@hooks/useTaskMembership';
import { Search } from '@root/types';
import theme from '@themes/theme';

interface ITaskSearch {
  search: string;
  data: Search<Task>[];
  toggleCompleteTask?: (task: Task) => void;
  loading?: boolean;
  sectionFooter?: JSX.Element;
  filterPart?: JSX.Element;
  hasError?: boolean;
  onPress?: (item: Task) => void;
  onEndReached?: (info: { distanceFromEnd: number }) => void;
}

const TasksSearch: React.FC<ITaskSearch> = ({
  search,
  data,
  toggleCompleteTask,
  loading = false,
  sectionFooter,
  filterPart,
  hasError = false,
  onPress,
  onEndReached,
}) => {
  const { t } = useTranslation('models');
  const { me } = useMe();
  const { modal: confirmModal, showConfirm } = useConfirmModal();
  const getStr = (task) => {
    if (task.completedAt && task.completedAt != null) {
      return 'Completed';
    } else {
      const value = task.dueDate;
      const day = dayjs(dayjs().format('MM/DD/YYYY')).diff(
        dayjs(dayjs(value).format('MM/DD/YYYY')),
        'day'
      );
      if (day > 0) return 'OverDue';
      if (day === 0) return 'Today';
      return 'Upcoming';
    }
  };
  const RenderItem = (item) => {
    const task = item.record;
    const [isHoveredTitle, setIsHoveredTitle] = useState(false);

    return (
      <Box
        paddingHorizontal='l'
        paddingVertical='xs'
        backgroundColor={isHoveredTitle ? 'grey01' : 'white'}
        onMouseEnter={() => setIsHoveredTitle(true)}
        onMouseLeave={() => setIsHoveredTitle(false)}>
        <TouchableOpacity onPress={() => onPress?.(task)}>
          <Box
            accessibilityLabel={task.name ? task.name : ''}
            flexDirection='row'
            alignItems='flex-start'>
            <Box marginRight='xs'>
              <RadioButton
                iconVariant='m'
                disabled={
                  !getTaskMembership(task, me?.id).canEditTaskThirdLevel
                }
                onPress={() => {
                  if (!!task.completedAt) {
                    showConfirm({
                      title: t('models:tasks.complete.reopenTask'),
                      message: t('models:tasks.complete.reopenTaskMsg'),
                      doNotShowCancel: true,
                      confirmText: t('models:tasks.complete.reopenTaskBtn'),
                      onConfirm: () => {
                        toggleCompleteTask?.(task);
                      },
                    });
                    toggleCompleteTask?.(task);
                  } else {
                    showConfirm({
                      title: t('models:tasks.complete.completeTask'),
                      message: t('models:tasks.complete.message_short'),
                      doNotShowCancel: true,
                      confirmText: t('models:tasks.complete.title'),
                      onConfirm: () => {
                        toggleCompleteTask?.(task);
                      },
                    });
                  }
                }}
                isSelected={task.completedAt && task.completedAt != null}
              />
            </Box>

            <Box flex={1} mr='m'>
              <Box justifyContent='space-between' flexDirection='row' flex={1}>
                <Box flex={1}>
                  <Text
                    accessibilityLabel='Task title'
                    variant='webBodySecondary'
                    textDecorationLine={
                      isHoveredTitle ? 'underline' : undefined
                    }
                    numberOfLines={1}
                    style={{ alignSelf: `flex-start` }}
                    marginRight='l'>
                    <Highlighter
                      autoEscape
                      highlightStyle={{
                        color: theme.colors.textPrimary,
                        backgroundColor: theme.colors.yellowBright,
                      }}
                      searchWords={search.split(' ')}
                      textToHighlight={task.name}
                    />
                  </Text>
                  {!!search && !!task.description && (
                    <Text
                      mt='xs'
                      accessibilityLabel='Task description'
                      variant='metadata'
                      color='grey04'
                      numberOfLines={1}
                      style={{ alignSelf: `flex-start` }}
                      marginRight='l'>
                      <Highlighter
                        autoEscape
                        highlightStyle={{
                          color: theme.colors.textPrimary,
                          backgroundColor: theme.colors.yellowBright,
                        }}
                        searchWords={search.split(' ')}
                        textToHighlight={task.description}
                      />
                    </Text>
                  )}
                  <Box flexDirection='row' alignItems='center' mt='xs'>
                    <Icon variant='s' color='grey04' name='Folder' />

                    <Text
                      variant='metadata'
                      color='grey04'
                      numberOfLines={1}
                      style={{ alignSelf: `flex-start` }}
                      marginLeft='xxs'>
                      {task.project?.name}
                    </Text>
                    <Box
                      mx='xxs'
                      backgroundColor='grey04'
                      style={{
                        width: 3,
                        height: 3,
                        borderRadius: 1.5,
                      }}></Box>
                    <Text
                      variant='metadata'
                      color='grey04'
                      numberOfLines={1}
                      style={{ alignSelf: `flex-start` }}>
                      {getStr(task)}
                    </Text>
                    {(!task.completedAt || task.completedAt == null) &&
                      task.dueDate &&
                      task.dueDate != null && (
                        <>
                          <Box
                            mx='xxs'
                            backgroundColor='grey04'
                            style={{
                              width: 3,
                              height: 3,
                              borderRadius: 1.5,
                            }}></Box>
                          <Text
                            variant='metadata'
                            color='grey04'
                            numberOfLines={1}
                            style={{ alignSelf: `flex-start` }}>
                            {t('format:abbrMonthAndDay', {
                              val: task.dueDate,
                            })}
                          </Text>
                        </>
                      )}
                  </Box>
                </Box>
              </Box>
            </Box>

            <Box
              flexDirection='row'
              height={40}
              alignItems='center'
              justifyContent='flex-end'>
              <Text
                marginRight='xs'
                numberOfLines={1}
                ellipsizeMode='tail'
                variant='webBodySecondary'>
                {task.project.team?.name}
              </Text>
              {task.project.team?.avatarFile ? (
                <CachedImage
                  avatarType='team'
                  avatarId={task.project.team.avatarFile.id}
                  image={task.project.team.avatarFile as unknown as LocalFile}
                  width={24}
                  height={24}
                  borderRadius={theme.spacing.xxs}
                />
              ) : (
                <Icon
                  name={task.project.team?.personal ? 'Office' : 'TTProject'}
                  variant='l'
                  color={task.project.team?.personal ? 'white' : undefined}
                  backgroundColor={
                    task.project.team?.personal ? 'greenSecondary' : 'black'
                  }
                  padding={task.project.team?.personal ? 'xxs' : 'none'}
                  width={task.project.team?.personal ? 16 : 24}
                  height={task.project.team?.personal ? 16 : 24}
                  borderRadius={task.project.team?.personal ? 'xxs' : 'xxs'}
                />
              )}
            </Box>
          </Box>
        </TouchableOpacity>
      </Box>
    );
  };

  return (
    <>
      {confirmModal}
      <GlobalSearchList
        loading={loading}
        search={search}
        isViewAllContacts={false}
        onEndReached={onEndReached}
        onPress={() => {}}
        hasError={hasError}
        // TaskRowSkeleton's default `paddingHorizontal='s'` is sized for its
        // other consumers (My Tasks / Project Tasks); Global Search's real
        // task row here uses `paddingHorizontal='l'` — match that via the
        // opt-in override rather than changing the shared default (TA-10278).
        skeletonRow={(index) => (
          <TaskRowSkeleton key={index} paddingHorizontal='l' />
        )}
        lists={[
          {
            title: 'Tasks',
            data: data,
            headerLeft: filterPart,
            totalCount: data.length > 0 ? data[0].total : data.length,
            render: RenderItem,
            itemSeparatorComponent: undefined,
            sectionFooter: sectionFooter,
          },
        ]}
      />
    </>
  );
};

export default TasksSearch;
